import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  deleteCron,
  getCronStatus,
  getCronStatusById,
  upsertCron,
} from "../../../api/cron";
import { ActionType, AppDispatch } from "../../../reducer";
import { DeleteBlockAction, InsertBlockAction } from "../../../reducer/page";
import { Cron } from "../../../types/cron";
import { CronBlock } from "../../../types/notebook/note/block/cron";
import { SubscriptionSearchResult } from "../../../types/subscriptionSearch";
import { isValidCronString } from "../../../util/cron";
import { useAppIdMap } from "../../app";
import { usePageBlocks, useSelectedNotebookPageIdMap } from "../../page";

const enrichCronBlock = (
  block: CronBlock,
  config: {
    name: string;
    schedule: string;
    subscription: SubscriptionSearchResult;
  },
): CronBlock => ({
  ...block,
  name: config.name,
  schedule: config.schedule,
  "subscription-page-id": config.subscription["page-id"],
  "subscription-name": config.subscription.name,
  "sub-id": config.subscription["sub-id"],
});

export const useInsertCronBlock = () => {
  const { "page-id": pageIdParam } = useParams();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const pageId = pageIdParam ?? selectedNotebookPageId;
  const { pageBlocks, setPageBlocks } = usePageBlocks(pageId);

  return async (config: {
    blockIndex: number;
    name: string;
    schedule: string;
    subscription: SubscriptionSearchResult;
  }) => {
    const block = await upsertCron({
      "page-id": pageId!,
      name: config.name,
      schedule: config.schedule,
      "subscription-page-id": config.subscription["page-id"],
      "subscription-name": config.subscription.name,
    });
    const blocks = pageBlocks ?? [];
    setPageBlocks([
      ...blocks.slice(0, config.blockIndex + 1),
      enrichCronBlock(block, config),
      ...blocks.slice(config.blockIndex + 1),
    ]);
    return block;
  };
};

export const useCronStatus = (block: CronBlock, name: string | undefined) => {
  const [cron, setCron] = useState<Cron | undefined>();

  useEffect(() => {
    if (name) {
      getCronStatus({ "page-id": block["page-id"], name }).then(setCron).catch(() => {});
      return;
    }
    getCronStatusById({
      "app-id": block["app-id"],
      "cron-id": block["cron-id"],
    }).then(setCron).catch(() => {});
  }, [block, name]);

  return { cron, setCron };
};

export const useCronEditor = (block: CronBlock) => {
  const [name, setNameLocal] = useState(block.name ?? "");
  const [schedule, setScheduleLocal] = useState(block.schedule ?? "");
  const [selectedSub, setSelectedSubLocal] = useState<SubscriptionSearchResult | undefined>(
    block["subscription-name"] && block["subscription-page-id"] && block["sub-id"]
      ? {
          name: block["subscription-name"],
          "page-id": block["subscription-page-id"],
          "sub-id": block["sub-id"],
          "block-id": block["block-id"],
          subscription: {
            type: "omega/query-omega/subscription/Sub",
            "sub-id": block["sub-id"],
            "page-id": block["subscription-page-id"],
            "app-id": block["app-id"],
            status: "started",
            clause: undefined,
            name: block["subscription-name"],
          },
          block: {
            type: "omega/query-omega/page/block/subscription/SubscriptionBlock",
            "sub-id": block["sub-id"],
            "block-id": block["block-id"],
            "page-id": block["subscription-page-id"],
            "app-id": block["app-id"],
          },
        }
      : undefined,
  );
  const { cron, setCron } = useCronStatus(block, name);

  useEffect(() => {
    if (!cron) return;
    setNameLocal(cron.name);
    setScheduleLocal(cron.schedule);
    setSelectedSubLocal((current) => ({
      name: cron["subscription-name"],
      "page-id": cron["subscription-page-id"],
      "sub-id": cron["sub-id"],
      "block-id": current?.["block-id"] ?? block["block-id"],
      subscription: current?.subscription ?? {
        type: "omega/query-omega/subscription/Sub",
        "sub-id": cron["sub-id"],
        "page-id": cron["subscription-page-id"],
        "app-id": cron["app-id"],
        status: "started",
        clause: undefined,
        name: cron["subscription-name"],
      },
      block: current?.block ?? {
        type: "omega/query-omega/page/block/subscription/SubscriptionBlock",
        "sub-id": cron["sub-id"],
        "block-id": block["block-id"],
        "page-id": cron["subscription-page-id"],
        "app-id": cron["app-id"],
      },
    }));
  }, [block, cron]);

  const writeBackend = useMemo(
    () =>
      debounce((next: {
        name: string;
        schedule: string;
        subscription: SubscriptionSearchResult;
      }) => {
        upsertCron({
          "page-id": block["page-id"],
          name: next.name,
          schedule: next.schedule,
          "subscription-page-id": next.subscription["page-id"],
          "subscription-name": next.subscription.name,
        }).then(() =>
          getCronStatus({ "page-id": block["page-id"], name: next.name }).then(setCron),
        );
      }, 500),
    [block, setCron],
  );

  const setName = (nextName: string) => {
    setNameLocal(nextName);
    if (!selectedSub || !nextName.trim() || !schedule.trim()) return;
    writeBackend({
      name: nextName.trim(),
      schedule: schedule.trim(),
      subscription: selectedSub,
    });
  };

  const setSchedule = (nextSchedule: string) => {
    setScheduleLocal(nextSchedule);
    if (!selectedSub || !name.trim() || !isValidCronString(nextSchedule)) return;
    writeBackend({
      name: name.trim(),
      schedule: nextSchedule.trim(),
      subscription: selectedSub,
    });
  };

  const setSelectedSubscription = (subscription: SubscriptionSearchResult) => {
    setSelectedSubLocal(subscription);
    if (!name.trim() || !schedule.trim()) return Promise.resolve();
    return upsertCron({
      "page-id": block["page-id"],
      name: name.trim(),
      schedule: schedule.trim(),
      "subscription-page-id": subscription["page-id"],
      "subscription-name": subscription.name,
    }).then(() =>
      getCronStatus({ "page-id": block["page-id"], name: name.trim() }).then(setCron),
    );
  };

  return {
    cron,
    name,
    setName,
    schedule,
    setSchedule,
    selectedSub,
    setSelectedSubscription,
  };
};

export const useDeleteCron = (block: CronBlock, name: string | undefined) => {
  const dispatch = useDispatch<AppDispatch>();
  const { selectedNotebookPageId } = useSelectedNotebookPageIdMap();
  const { appId } = useAppIdMap();

  return async () => {
    if (!name || !selectedNotebookPageId || !appId) return;
    await deleteCron({ "page-id": block["page-id"], name });
    const action: DeleteBlockAction = {
      type: ActionType.DELETE_BLOCK,
      focusedIndex: block["block-index"] ?? 0,
    };
    dispatch(action as any);
  };
};
